import { z } from 'zod';
// ================================
// Schemas (avoid circular dependency with index.ts)
// ================================
const isoDateTimeSchema = z
    .string()
    .datetime({ message: 'Expected an ISO 8601 timestamp' });
// ================================
// Platform-Level Permissions
// ================================
/**
 * Platform-level permissions that transcend group boundaries
 */
export const platformPermissionValues = [
    'platform.admin.manage', // Manage platform admins
    'platform.groups.create', // Create groups without approval
    'platform.groups.approve', // Approve group creation requests
    'platform.groups.delete', // Delete any group
    'platform.users.manage', // Manage all users
    'platform.content.broadcast', // Send platform-wide broadcasts
];
/**
 * Check if a user has a specific platform permission
 * Currently all platform permissions require admin status
 * @param user - User object with isAdmin flag
 * @param permission - The permission to check (reserved for future granular permissions)
 */
export const hasPlatformPermission = (user, permission // eslint-disable-line @typescript-eslint/no-unused-vars
) => {
    // Platform admins have all platform permissions
    return user.isAdmin === true;
};
// ================================
// Group-Level Permissions
// ================================
/**
 * Group-level permissions for specific group operations
 */
export const groupPermissionValues = [
    'group.settings.update', // Update group settings
    'group.members.approve', // Approve member requests
    'group.members.remove', // Remove members
    'group.meetings.create', // Create meetings for the group
    'group.meetings.update', // Update meetings
    'group.meetings.delete', // Delete meetings
    'group.messages.send', // Send messages to group
];
/**
 * Group member roles - simplified to organizer and member
 */
export const groupRoleValues = ['organizer', 'member'];
export const groupRoleSchema = z.enum(groupRoleValues);
/**
 * Map group roles to their permissions
 */
export const GROUP_ROLE_PERMISSIONS = {
    organizer: [
        'group.settings.update',
        'group.members.approve',
        'group.members.remove',
        'group.meetings.create',
        'group.meetings.update',
        'group.meetings.delete',
        'group.messages.send',
    ],
    member: [], // Regular members have no special permissions
};
/**
 * Check if a user has a specific group permission
 */
export const hasGroupPermission = (member, permission, isPlatformAdmin = false) => {
    // Platform admins bypass all group-level permission checks
    if (isPlatformAdmin)
        return true;
    // Check if member exists and has the permission through their role
    if (!member)
        return false;
    return GROUP_ROLE_PERMISSIONS[member.role].includes(permission);
};
// ================================
// Group Creation Request Types
// ================================
export const groupCreationRequestStatusValues = ['pending', 'approved', 'rejected'];
export const groupCreationRequestStatusSchema = z.enum(groupCreationRequestStatusValues);
export const groupCreationRequestSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(80),
    slug: z.string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9-]+$/, {
        message: 'Slug must include lowercase letters, numbers, or hyphen only'
    }),
    summary: z.string().max(280).optional(),
    autoAcceptMembers: z.boolean(),
    requesterUid: z.string().min(1),
    requesterName: z.string().min(1),
    requesterEmail: z.string().email(),
    requestedAt: isoDateTimeSchema,
    status: groupCreationRequestStatusSchema,
    reviewedBy: z.string().min(1).optional(),
    reviewerName: z.string().min(1).optional(), // Denormalized reviewer display name
    reviewedAt: isoDateTimeSchema.optional(),
    reviewNote: z.string().max(500).optional(),
    groupId: z.string().min(1).optional(), // Reference to created group if approved
});
export const groupCreationRequestWriteSchema = groupCreationRequestSchema
    .omit({ id: true, requestedAt: true, status: true })
    .extend({
    requestedAt: isoDateTimeSchema.optional(),
    status: groupCreationRequestStatusSchema.optional(),
});
export const assertGroupCreationRequest = (input) => groupCreationRequestSchema.parse(input);
/**
 * Comprehensive permission checker
 */
export class PermissionChecker {
    context;
    constructor(context) {
        this.context = context;
    }
    // Platform-level checks
    canManagePlatformAdmins() {
        return this.context.user ? hasPlatformPermission(this.context.user, 'platform.admin.manage') : false;
    }
    canCreateGroupsWithoutApproval() {
        return this.context.user ? hasPlatformPermission(this.context.user, 'platform.groups.create') : false;
    }
    canApproveGroupCreationRequests() {
        return this.context.user ? hasPlatformPermission(this.context.user, 'platform.groups.approve') : false;
    }
    canDeleteAnyGroup() {
        return this.context.user ? hasPlatformPermission(this.context.user, 'platform.groups.delete') : false;
    }
    canSendPlatformBroadcasts() {
        return this.context.user ? hasPlatformPermission(this.context.user, 'platform.content.broadcast') : false;
    }
    // Group-level checks
    canUpdateGroupSettings() {
        return hasGroupPermission(this.context.groupMember, 'group.settings.update', this.context.user?.isAdmin === true);
    }
    canApproveGroupMembers() {
        return hasGroupPermission(this.context.groupMember, 'group.members.approve', this.context.user?.isAdmin === true);
    }
    canRemoveGroupMembers() {
        return hasGroupPermission(this.context.groupMember, 'group.members.remove', this.context.user?.isAdmin === true);
    }
    canCreateGroupMeetings() {
        return hasGroupPermission(this.context.groupMember, 'group.meetings.create', this.context.user?.isAdmin === true);
    }
    canUpdateGroupMeetings() {
        return hasGroupPermission(this.context.groupMember, 'group.meetings.update', this.context.user?.isAdmin === true);
    }
    canDeleteGroupMeetings() {
        return hasGroupPermission(this.context.groupMember, 'group.meetings.delete', this.context.user?.isAdmin === true);
    }
    canSendGroupMessages() {
        return hasGroupPermission(this.context.groupMember, 'group.messages.send', this.context.user?.isAdmin === true);
    }
    // Composite checks
    canManageGroup() {
        return this.canUpdateGroupSettings();
    }
    isGroupOrganizer() {
        return this.context.groupMember?.role === 'organizer';
    }
    isActiveMember() {
        return this.context.groupMember?.status === 'active';
    }
    isPlatformAdmin() {
        return this.context.user?.isAdmin === true;
    }
}
// ================================
// Migration Utilities
// ================================
/**
 * Check if user should be migrated to platform admin based on canonical organizer status
 */
export const shouldMigrateToPlatformAdmin = (uid, email, canonicalUids, canonicalEmails) => {
    return canonicalUids.includes(uid) || canonicalEmails.includes(email);
};
/**
 * Determine group role based on legacy user role
 */
export const mapLegacyRoleToGroupRole = (legacyRole) => {
    return legacyRole === 'admin' || legacyRole === 'organizer' ? 'organizer' : 'member';
};
